import api from './api';

class PermissionsService {
  // Cache de permissões
  static permissionsCache = null;
  static cacheExpiry = null;

  // Verifica se o cache é válido (5 minutos)
  static isCacheValid() {
    if (!this.permissionsCache || !this.cacheExpiry) return false;
    return Date.now() < this.cacheExpiry;
  }

  // Busca permissões do usuário
  static async getUserPermissions() {
    if (this.isCacheValid()) {
      return this.permissionsCache;
    }

    try {
      const response = await api.get('/auth/profile');
      this.permissionsCache = response.data;
      this.cacheExpiry = Date.now() + (5 * 60 * 1000); // 5 minutos
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar permissões:', error);
      return null;
    }
  }

  // Limpa o cache
  static clearCache() {
    this.permissionsCache = null;
    this.cacheExpiry = null;
  }

  // Verifica permissão específica
  static hasPermission(user, module, action = 'view') {
    if (!user) return false;

    // Admin tem acesso total
    if (user.cargo?.permissoes?.all === true) return true;

    // Verifica permissões do usuário
    if (user.permissoes?.[module]?.includes(action)) return true;

    // Verifica permissões do cargo
    if (user.cargo?.permissoes?.[module]?.includes(action)) return true;

    return false;
  }

  // Filtra dados baseado em permissões
  static filterByPermissions(data, module, user) {
    if (!user) return [];
    
    // Admin vê tudo
    if (user.cargo?.permissoes?.all === true) return data;

    // Filtra baseado em regras específicas do módulo
    switch (module) {
      case 'clientes':
        // Se tem permissão de view, vê todos
        if (this.hasPermission(user, 'clientes', 'view')) return data;
        // Senão, vê apenas seus próprios clientes
        return data.filter(item => item.responsavelId === user.id);
        
      case 'chamados':
        // Técnico vê apenas chamados atribuídos a ele
        if (user.cargo?.nome?.toLowerCase().includes('técnico')) {
          return data.filter(item => item.tecnicoId === user.id);
        }
        return data;
        
      case 'atendimentos':
        // Atendente vê apenas seus próprios atendimentos
        if (user.cargo?.nome?.toLowerCase().includes('atendente')) {
          return data.filter(item => item.usuarioId === user.id);
        }
        return data;
        
      default:
        return data;
    }
  }

  // Obtém módulos disponíveis para o usuário
  static getAvailableModules(user) {
    const allModules = [
      'clientes', 'contratos', 'planos', 'vouchers', 'promocoes',
      'chamados', 'atendimentos', 'ordens_servico', 
      'boletos', 'movimentacoes', 'equipamentos',
      'usuarios', 'cargos', 'empresas', 'logs'
    ];

    if (!user) return [];

    // Admin tem acesso a todos
    if (user.cargo?.permissoes?.all === true) return allModules;

    // Filtra módulos baseado em permissões
    return allModules.filter(module => {
      return this.hasPermission(user, module, 'view');
    });
  }

  // Obtém ações disponíveis para um módulo
  static getAvailableActions(user, module) {
    const allActions = ['view', 'create', 'edit', 'delete'];
    
    if (!user || !module) return [];

    // Admin tem todas as ações
    if (user.cargo?.permissoes?.all === true) return allActions;

    const availableActions = [];

    // Verifica cada ação
    for (const action of allActions) {
      if (this.hasPermission(user, module, action)) {
        availableActions.push(action);
      }
    }

    return availableActions;
  }
}

export default PermissionsService;